import React from 'react';
import {
  Divider,
  IconButton,
  Typography,
  Button,
  Collapse,
  CardContent,
  Card,
  CardHeader,
  Grid
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import { PAGE_PATHS } from '@/constants/constants';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type Stripe from 'stripe';

const LineItem = ({
  lineItem,
  initialExpanded,
  disableExpand,
  onChangeLink
}: {
  lineItem: StripeLineItemWithPriceProduct;
  initialExpanded: boolean;
  disableExpand?: boolean;
  onChangeLink?: string;
}) => {
  const [expanded, setExpanded] = React.useState(initialExpanded);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const name = lineItem.price.product.name;
  const product = lineItem.price.product as StripeProductWithPrice;

  const tickIcon = (
    <span>
      <DoneIcon
        fontSize='small'
        sx={{
          color: '#F4A71C',
          verticalAlign: 'text-bottom',
          marginRight: '10px'
        }}
      />
    </span>
  );

  const getStripePaymentFrequency = (price: Stripe.Price) => {
    if (price?.recurring?.interval === 'month') {
      if (price?.recurring?.interval_count === 1) {
        return 'Per Month';
      } else if (price?.recurring?.interval_count === 3) {
        return `Per Quarter`;
      }
    }
    return '';
  };

  const lineItemType = lineItem.price.type;
  const isRecurring = lineItemType === 'recurring';

  const title = `${name}${isRecurring ? ' Plan' : ''}`;
  const priceLabel = isRecurring
    ? getStripePaymentFrequency(lineItem.price)
    : null;

  const getPrice = (lineItem: Stripe.LineItem) => {
    const unitAmount = lineItem.price?.unit_amount ?? 0;
    const quantity = lineItem.quantity ?? 1;
    const price = (unitAmount * quantity) / 100;
    const plusGST = ['exclusive'].includes(
      lineItem.price?.tax_behavior || 'exclusive'
    );
    return `$${price.toFixed(2)}${plusGST ? ' + GST' : ''}`;
  };

  const price = getPrice(lineItem);

  return (
    <Card sx={{ background: '#FAFAFA' }}>
      <CardHeader
        title={title}
        titleTypographyProps={{ variant: 'h1', pb: 1 }}
        subheader={
          <Grid container>
            <Grid item xs={10}>
              <Typography variant='h4' fontSize={18}>
                {price}
              </Typography>

              {priceLabel && (
                <Typography variant='body1' fontSize={18}>
                  {priceLabel}
                </Typography>
              )}
            </Grid>
            <Grid item xs>
              {!disableExpand && (
                <IconButton
                  onClick={handleExpandClick}
                  sx={{
                    marginLeft: 'auto',
                    transform: !expanded ? 'rotate(0deg)' : 'rotate(180deg)'
                  }}
                >
                  <ExpandMoreIcon />
                </IconButton>
              )}
            </Grid>
          </Grid>
        }
      />
      <Collapse in={expanded} timeout='auto' unmountOnExit>
        <CardContent sx={{ pt: 0 }}>
          {!isRecurring && (
            <>
              <Divider sx={{ mb: 2 }} />
              <Typography variant='body1' component='span'>
                {product.description}
              </Typography>
            </>
          )}
          {product.marketing_features?.length > 0 && (
            <>
              <Divider sx={{ mb: 2 }} />
              <Typography variant='h4'>WHAT'S INCLUDED:</Typography>
              <Typography variant='body1' component='span'>
                <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                  {product.marketing_features.map((feature, idx) => (
                    <li key={idx}>
                      {tickIcon}
                      {feature.name}
                    </li>
                  ))}
                </ul>
              </Typography>
            </>
          )}
          {isRecurring && (
            <>
              <Button
                variant='outlined'
                href={onChangeLink || PAGE_PATHS.membershipPlans}
                color='secondary'
                sx={{
                  mb: 2,
                  backgroundColor: 'white',
                  width: '100%'
                }}
              >
                CHANGE PLAN
              </Button>
              <Typography variant='caption'>
                Membership renews automatically at the end of the billing cycle.
                Cancel anytime.
              </Typography>
            </>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default LineItem;
